<template>
  <div>
    <LoadingContainer v-if="loading"/>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Edit Ad
            </h3>

            <p>
              Edit the details of the ad.
            </p>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <SelectFormGroup
                    v-model="data.time_based_ad_format_id"
                    name="time_based_ad_format_id"
                    label="Ad Format"
                    help-text="The ad format."
                    :error="getError('time_based_ad_format_id')"
                    :options="timeBasedAdFormatOptions"
                    :disabled="timeBasedAd.attributes.is_locked && missingPermission('admin')"
                />

                <TableFormGroup
                    v-model="selectedTimeBasedAdLocations"
                    name="time_based_ad_locations"
                    label="Locations"
                    help-text="The locations that the ad should be placed in."
                    :error="getError('time_based_ad_locations')"
                    :options="timeBasedAdLocationOptions"
                    option-label="Location"
                    :disabled="timeBasedAd.attributes.is_locked && missingPermission('admin')"
                />

                <DateFormGroup
                    v-model="data.starts_at"
                    name="starts_at"
                    label="Starts At"
                    help-text="The day that the ad starts."
                    :error="getError('starts_at')"
                    :disabled="timeBasedAd.attributes.is_locked && missingPermission('admin')"
                    format="DD.MM.YYYY"
                />

                <DateFormGroup
                    v-model="data.ends_at"
                    name="ends_at"
                    label="Ends At"
                    help-text="The day that the ad ends."
                    :error="getError('ends_at')"
                    :disabled="timeBasedAd.attributes.is_locked && missingPermission('admin')"
                    format="DD.MM.YYYY"
                />

                <DateFormGroup
                    v-model="data.content_deadline_at"
                    name="content_deadline_at"
                    label="Content Deadline At"
                    help-text="The deadline for receiving content for the ad."
                    :error="getError('content_deadline_at')"
                    :disabled="missingPermission('admin')"
                    format="DD.MM.YYYY"
                />

                <DateFormGroup
                    v-model="data.content_deadline_reminder_at"
                    name="content_deadline_reminder_at"
                    label="Content Deadline Reminder At"
                    help-text="The date that the reminders for submitting ad content should be sent out."
                    :error="getError('content_deadline_reminder_at')"
                    :disabled="missingPermission('admin')"
                    format="DD.MM.YYYY"
                />

                <SelectFormGroup
                    v-model="data.project_id"
                    name="project_id"
                    label="Project"
                    help-text="The project that the ad belongs to."
                    :options="projectOptions"
                    :nullable="true"
                />

                <NumberFormGroup
                    v-model="data.price"
                    name="price"
                    label="Price"
                    help-text="The price of the ad."
                    :error="getError('price')"
                    :disabled="timeBasedAd.attributes.is_locked && missingPermission('admin')"
                />

                <NumberFormGroup
                    v-model="data.agency_commission"
                    name="agency_commission"
                    label="Agency Commission"
                    help-text="The agency commission of the ad."
                    :error="getError('agency_commission')"
                    :disabled="timeBasedAd.attributes.is_locked && missingPermission('admin')"
                />

                <NumberFormGroup
                    v-model="data.third_party_costs"
                    name="third_party_costs"
                    label="Third Party Costs"
                    help-text="The third party costs of the ad."
                    :error="getError('third_party_costs')"
                    :disabled="timeBasedAd.attributes.is_locked && missingPermission('admin')"
                />

                <TextFormGroup
                    v-model="data.url"
                    name="url"
                    label="URL"
                    help-text="The URL that the ad should point at (optional)."
                    :error="getError('url')"
                />

                <TextFormGroup
                    v-model="data.content"
                    name="content"
                    label="Material Number"
                    help-text="The material number for the ad (optional)."
                    :error="getError('content')"
                />

                <TextFormGroup
                    v-model="data.dtp_comment"
                    name="dtp_comment"
                    label="DTP Comment"
                    help-text="The comment for the DTP (optional)."
                    :error="getError('dtp_comment')"
                />

                <TextFormGroup
                    v-model="data.customer_comment"
                    name="customer_comment"
                    label="Customer Comment"
                    help-text="The comment for the customer (optional)."
                    :error="getError('customer_comment')"
                />

                <hr>

                <div class="form-group">
                  <div class="col-sm-offset-8 col-sm-12">
                    <button type="submit" class="btn btn-black">
                      <i class="fa fa-save"></i> Save
                    </button>

                    <button
                        type="button"
                        class="btn btn-white pull-right"
                        style="border: 1px solid #000000;"
                        data-toggle="modal"
                        data-target="#find-ad-modal"
                    >
                      <i class="fa fa-search"></i> Find DTP Info
                    </button>

                    <div class="clearfix"></div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <FindAdModal :ads="ads" @select="selectAd"/>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import moment from 'moment'
import notify from '@/notify'
import DateFormGroup from '@/vendor/forms/FormGroups/Horizontal/Date'
import Errors from '@/mixins/Errors'
import FindAdModal from '@/pages/Companies/Companies/Edit/Modals/FindAd'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import Permissions from '@/mixins/Permissions'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import StatusLabel from '@/components/StatusLabel'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TableFormGroup from '@/components/FormGroups/Horizontal/Table'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'

export default {
  components: {
    DateFormGroup,
    FindAdModal,
    HelpText,
    LoadingContainer,
    NumberFormGroup,
    SelectFormGroup,
    StatusLabel,
    SubmitFormGroup,
    TableFormGroup,
    TextFormGroup
  },

  mixins: [Errors, Permissions],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company', 'orderId', 'timeBasedAdId'],

  data() {
    return {
      timeBasedAd: null,
      data: null,

      timeBasedAdFormats: null,
      ads: null,
      timeBasedAdLocations: null,
      projects: null,
      selectedTimeBasedAdLocations: null,

      loading: true,
    }
  },

  watch: {
    'data.starts_at'(value, oldValue) {
      if (!oldValue) {
        return
      }

      this.data.content_deadline_at = moment(value)
          .subtract(this.data.default_content_deadline_period, 'days')
          .format('YYYY-MM-DD')
    },

    'data.content_deadline_at'(value, oldValue) {
      if (!oldValue) {
        return
      }

      this.data.content_deadline_reminder_at = moment(value)
          .subtract(this.data.default_content_deadline_reminder_period, 'days')
          .format('YYYY-MM-DD')
    }
  },

  mounted() {
    Promise.all([
      http.get('/api/v2/time-based-ads/' + this.timeBasedAdId),
      http.get('/api/v1/ads', {
        company: this.company.data.id,
        starts_from: moment().subtract(1, 'year').format('YYYY-MM-DD')
      }),
      http.getList('/api/v2/time-based-ad-formats'),
      http.getList('/api/v2/time-based-ad-locations'),
      http.getList('/api/v2/projects',{'filter[status]': 'active'}),
    ])
        .then(responses => {
          this.timeBasedAd = responses[0].data.data

          this.data = responses[0].data.data.attributes

          this.ads = responses[1].data

          this.timeBasedAdFormats = responses[2]

          this.timeBasedAdLocations = responses[3]

          this.projects = responses[4]

          this.selectedTimeBasedAdLocations = this.data.time_based_ad_locations.map(location => location.id)

          this.loading = false
        })
  },

  computed: {
    timeBasedAdFormatOptions() {
      return this.timeBasedAdFormats
          .filter(timeBasedAdFormat => timeBasedAdFormat.attributes.time_based_channel_id == this.timeBasedAd.attributes.time_based_channel_id)
          .map(timeBasedAdFormat => {
            return {
              value: timeBasedAdFormat.id,
              label: timeBasedAdFormat.attributes.name
            }
          })
    },

    timeBasedAdLocationOptions() {
      return this.timeBasedAdLocations
          .filter(timeBasedAdLocation => timeBasedAdLocation.attributes.time_based_channel_id == this.timeBasedAd.attributes.time_based_channel_id)
          .map(timeBasedAdLocation => {
            return {
              value: timeBasedAdLocation.id,
              label: timeBasedAdLocation.attributes.name
            }
          })
    },

    projectOptions() {
      return this.projects.map(project => ({value: project.id, label: project.attributes.portal_name + ' - ' +project.attributes.project_category_name + ' - ' + project.attributes.name}))
    },
  },

  methods: {
    submit() {
      http.put('/api/v2/time-based-ads/' + this.timeBasedAd.id, {
        ...this.data,
        starts_at: moment(this.data.starts_at).format('YYYY-MM-DD'),
        ends_at: moment(this.data.ends_at).format('YYYY-MM-DD'),
        content_deadline_at: moment(this.data.content_deadline_at).format('YYYY-MM-DD'),
        content_deadline_reminder_at: moment(this.data.content_deadline_reminder_at).format('YYYY-MM-DD'),
        time_based_ad_locations: this.selectedTimeBasedAdLocations.map(location => {
          return {id: location}
        })
      })
          .then(() => {
            notify.success('The ad was saved')

            http.get('/api/v1/companies/' + this.company.data.id)
                .then(response => {
                  this.$emit('company-updated', response.data)

                  this.$router.push('..')
                })
          })
          .catch(this.catchValidationErrors)
    },

    selectAd(ad) {
      this.data = {
        ...this.data,
        url: ad.url,
        content: ad.content
      }
    }
  }
}
</script>
